import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Users, Package, UtensilsCrossed, LogIn, Download, Printer, Eye, RefreshCw, Loader2 } from "lucide-react";

const API = '/api';

const baseStyle = 'body{font-family:Arial,sans-serif;margin:20px;color:#222}h1{color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:8px}h2{color:#1e3a5f;margin-top:25px}table{width:100%;border-collapse:collapse;margin:15px 0;font-size:13px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#1e3a5f;color:#fff}.stats{display:flex;gap:15px;margin:20px 0;flex-wrap:wrap}.stat-box{background:#f4f6f9;padding:15px 20px;border-radius:8px;text-align:center;flex:1;min-width:140px;border-left:4px solid #1e3a5f}.stat-box h3{margin:0;color:#1e3a5f;font-size:13px;text-transform:uppercase}.stat-box p{font-size:26px;font-weight:bold;margin:6px 0;color:#0f172a}.meta{color:#64748b;font-size:12px;margin-bottom:10px}@media print{body{margin:10mm}}';

const wrapHTML = (title, body) => '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>'+title+'</title><style>'+baseStyle+'</style></head><body>'+body+'</body></html>';

const genInventarioHTML = (data) => {
  const mezzi = data?.mezzi || data?.veicoli || [];
  const tende = data?.tende || [];
  const rowsMezzi = mezzi.map(x => '<tr><td>'+(x.tipo||'')+'</td><td>'+(x.targa||'')+'</td><td>'+(x.stato||'')+'</td><td>'+(x.organizzazione||'')+'</td></tr>').join('');
  const rowsTende = tende.map(x => '<tr><td>'+(x.codice||'')+'</td><td>'+(x.tipo||'')+'</td><td>'+(x.capienza||'')+'</td><td>'+(x.stato||'')+'</td></tr>').join('');
  const body = '<h1>Inventario Risorse</h1><p class="meta">Generato: '+new Date().toLocaleString('it-IT')+'</p><h2>Mezzi ('+mezzi.length+')</h2><table><tr><th>Tipo</th><th>Targa</th><th>Stato</th><th>Organizzazione</th></tr>'+rowsMezzi+'</table><h2>Tende ('+tende.length+')</h2><table><tr><th>Codice</th><th>Tipo</th><th>Capienza</th><th>Stato</th></tr>'+rowsTende+'</table>';
  return wrapHTML('Inventario Risorse', body);
};

const genVolontariHTML = (data) => {
  const volontari = data?.volontari || [];
  const rows = volontari.map(x => '<tr><td>'+(x.cognome||'')+'</td><td>'+(x.nome||'')+'</td><td>'+(x.specializzazione||'')+'</td><td>'+(x.organizzazione||'')+'</td></tr>').join('');
  const body = '<h1>Elenco Volontari</h1><p class="meta">Generato: '+new Date().toLocaleString('it-IT')+' - Totale: '+volontari.length+'</p><table><tr><th>Cognome</th><th>Nome</th><th>Specializzazione</th><th>Organizzazione</th></tr>'+rows+'</table>';
  return wrapHTML('Elenco Volontari', body);
};

const genAccessiHTML = (data) => {
  const presenti = data?.presenti || [];
  const cronologia = data?.cronologia || [];
  const rowsPresenti = presenti.map(x => '<tr><td>'+(x.cognome||'')+'</td><td>'+(x.nome||'')+'</td><td>'+(x.organizzazione||'')+'</td><td>'+(x.ora_ingresso ? x.ora_ingresso.slice(11,16) : '')+'</td></tr>').join('');
  const rowsCrono = cronologia.map(x => '<tr><td>'+(x.timestamp ? x.timestamp.slice(11,16) : '')+'</td><td>'+(x.tipo||'')+'</td><td>'+((x.cognome||'')+' '+(x.nome||'')).trim()+'</td><td>'+(x.organizzazione||'')+'</td></tr>').join('');
  const body = '<h1>Situazione Accessi</h1><p class="meta">Generato: '+new Date().toLocaleString('it-IT')+'</p><div class="stats"><div class="stat-box"><h3>Presenti Ora</h3><p>'+(data?.presenti_ora||presenti.length||0)+'</p></div><div class="stat-box"><h3>Entrate Oggi</h3><p>'+(data?.entrate_oggi||0)+'</p></div><div class="stat-box"><h3>Uscite Oggi</h3><p>'+(data?.uscite_oggi||0)+'</p></div></div><h2>Persone Presenti ('+presenti.length+')</h2><table><tr><th>Cognome</th><th>Nome</th><th>Organizzazione</th><th>Ora Ingresso</th></tr>'+rowsPresenti+'</table><h2>Ultimi Movimenti</h2><table><tr><th>Ora</th><th>Tipo</th><th>Persona</th><th>Organizzazione</th></tr>'+rowsCrono+'</table>';
  return wrapHTML('Situazione Accessi', body);
};

const genSituazioneHTML = (data) => {
  const d = data || {};
  const body = '<h1>Situazione Giornaliera Campo</h1><p class="meta">Generato: '+new Date().toLocaleString('it-IT')+'</p><div class="stats"><div class="stat-box"><h3>Volontari</h3><p>'+(d.volontari_totali||d.totale_volontari||0)+'</p></div><div class="stat-box"><h3>Presenti</h3><p>'+(d.presenti_ora||d.presenti||0)+'</p></div><div class="stat-box"><h3>Organizzazioni</h3><p>'+(d.organizzazioni||0)+'</p></div><div class="stat-box"><h3>Mezzi</h3><p>'+(d.mezzi||d.veicoli||0)+'</p></div><div class="stat-box"><h3>Tende</h3><p>'+(d.tende||0)+'</p></div><div class="stat-box"><h3>Pasti Oggi</h3><p>'+(d.pasti_oggi||0)+'</p></div></div><h2>Riepilogo Operativo</h2><table><tr><th>Voce</th><th>Valore</th></tr><tr><td>Entrate oggi</td><td>'+(d.entrate_oggi||0)+'</td></tr><tr><td>Uscite oggi</td><td>'+(d.uscite_oggi||0)+'</td></tr><tr><td>Operazioni attive</td><td>'+(d.operazioni_attive||0)+'</td></tr><tr><td>Badge emessi</td><td>'+(d.badge_emessi||0)+'</td></tr></table>';
  return wrapHTML('Situazione Giornaliera', body);
};

const genPastiHTML = (data) => {
  const giorni = data?.giorni || data?.giornalieri || [];
  const rows = giorni.map(g => '<tr><td>'+(g.data||g.date||'')+'</td><td>'+(g.colazione||0)+'</td><td>'+(g.pranzo||0)+'</td><td>'+(g.cena||0)+'</td><td>'+((g.colazione||0)+(g.pranzo||0)+(g.cena||0))+'</td></tr>').join('');
  const totC = giorni.reduce((s,g)=>s+(g.colazione||0),0);
  const totP = giorni.reduce((s,g)=>s+(g.pranzo||0),0);
  const totCe = giorni.reduce((s,g)=>s+(g.cena||0),0);
  const body = '<h1>Report Pasti</h1><p class="meta">Generato: '+new Date().toLocaleString('it-IT')+'</p><div class="stats"><div class="stat-box"><h3>Colazioni</h3><p>'+totC+'</p></div><div class="stat-box"><h3>Pranzi</h3><p>'+totP+'</p></div><div class="stat-box"><h3>Cene</h3><p>'+totCe+'</p></div><div class="stat-box"><h3>Totale</h3><p>'+(totC+totP+totCe)+'</p></div></div><h2>Dettaglio giornaliero</h2><table><tr><th>Data</th><th>Colazione</th><th>Pranzo</th><th>Cena</th><th>Totale</th></tr>'+rows+'</table>';
  return wrapHTML('Report Pasti', body);
};

const REPORTS = {
  situazione: { title: 'Situazione Giornaliera', desc: 'Riepilogo campo', icon: FileText, endpoint: '/stats/dashboard', gen: genSituazioneHTML },
  volontari:  { title: 'Elenco Volontari', desc: 'Lista volontari completa', icon: Users, endpoint: '/reports/volontari', gen: genVolontariHTML },
  art39:     { title: 'Volontari Art.39', desc: 'Solo richiedenti benefici Art.39 (L.225/92)', icon: Users, endpoint: '/reports/volontari?art_39=true', gen: genVolontariHTML },
  inventario: { title: 'Inventario Risorse', desc: 'Mezzi e tende', icon: Package, endpoint: '/reports/inventario', gen: genInventarioHTML },
  pasti:      { title: 'Report Pasti', desc: 'Statistiche pasti giornalieri', icon: UtensilsCrossed, endpoint: '/stats/pasti-giornalieri', gen: genPastiHTML },
  accessi:    { title: 'Situazione Accessi', desc: 'Entrate/uscite campo', icon: LogIn, endpoint: '/reports/accessi', gen: genAccessiHTML },
};

async function fetchReport(id) {
  const r = REPORTS[id];
  if (!r) throw new Error('Report non trovato');
  const res = await fetch(API + r.endpoint, { credentials: 'include' });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return await res.json();
}

export default function Reportistica() {
  const [loading, setLoading] = useState({});
  const [message, setMessage] = useState(null);
  const [data, setData] = useState({});
  const [preview, setPreview] = useState(null);

  const setL = (k, v) => setLoading(p => ({ ...p, [k]: v }));

  const loadAll = useCallback(async () => {
    setL('all', true);
    setMessage(null);
    const results = {};
    for (const id of Object.keys(REPORTS)) {
      try { results[id] = await fetchReport(id); }
      catch (e) { results[id] = { __error: e.message }; }
    }
    setData(results);
    setL('all', false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doDownload = async (id) => {
    setL('d-'+id, true); setMessage(null);
    try {
      const d = data[id] && !data[id].__error ? data[id] : await fetchReport(id);
      const html = REPORTS[id].gen(d);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = id + '-' + new Date().toISOString().slice(0,10) + '.html';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage({ type: 'ok', text: 'Download avviato: ' + id });
    } catch (e) { setMessage({ type: 'err', text: 'Errore download: ' + e.message }); }
    finally { setL('d-'+id, false); }
  };

  const doPrint = async (id) => {
    setL('p-'+id, true); setMessage(null);
    try {
      const d = data[id] && !data[id].__error ? data[id] : await fetchReport(id);
      const html = REPORTS[id].gen(d);
      const win = window.open('', '_blank');
      if (!win) throw new Error('Popup bloccato dal browser');
      win.document.write(html); win.document.close();
      setTimeout(() => { try { win.print(); } catch(e){} }, 300);
    } catch (e) { setMessage({ type: 'err', text: 'Errore stampa: ' + e.message }); }
    finally { setL('p-'+id, false); }
  };

  const doPreview = async (id) => {
    setL('v-'+id, true); setMessage(null);
    try {
      const d = await fetchReport(id);
      setData(prev => ({ ...prev, [id]: d }));
      setPreview({ id, html: REPORTS[id].gen(d) });
    } catch (e) { setMessage({ type: 'err', text: 'Errore anteprima: ' + e.message }); }
    finally { setL('v-'+id, false); }
  };

  const kpi = data.situazione && !data.situazione.__error ? data.situazione : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reportistica</h1>
          <p className="text-sm text-gray-500">Generazione report operativi del campo</p>
        </div>
        <Button onClick={loadAll} disabled={loading.all} variant="outline">
          {loading.all ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <RefreshCw className="h-4 w-4 mr-2"/>}
          Aggiorna dati
        </Button>
      </div>

      {message && (
        <div className={'p-3 rounded text-sm ' + (message.type === 'err' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800')}>
          {message.text}
        </div>
      )}

      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            ['Volontari', kpi.volontari_totali || kpi.totale_volontari || 0],
            ['Presenti', kpi.presenti_ora || kpi.presenti || 0],
            ['Organizzazioni', kpi.organizzazioni || 0],
            ['Mezzi', kpi.mezzi || kpi.veicoli || 0],
            ['Tende', kpi.tende || 0],
            ['Pasti oggi', kpi.pasti_oggi || 0],
          ].map(([label, val]) => (
            <Card key={label}>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 uppercase">{label}</div>
                <div className="text-2xl font-bold text-[#1e3a5f]">{val}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(REPORTS).map(([id, r]) => {
          const Icon = r.icon;
          const err = data[id]?.__error;
          return (
            <Card key={id} className={err ? 'border-red-300' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Icon className="h-5 w-5"/>{r.title}</CardTitle>
                <CardDescription>{r.desc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {err && <div className="text-xs text-red-600">Errore: {err}</div>}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => doPreview(id)} disabled={loading['v-'+id]}>
                    {loading['v-'+id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Eye className="h-4 w-4 mr-1"/>}Anteprima
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doDownload(id)} disabled={loading['d-'+id]}>
                    {loading['d-'+id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Download className="h-4 w-4 mr-1"/>}Scarica
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doPrint(id)} disabled={loading['p-'+id]}>
                    {loading['p-'+id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Printer className="h-4 w-4 mr-1"/>}Stampa/PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-semibold">Anteprima: {REPORTS[preview.id].title}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => doDownload(preview.id)}><Download className="h-4 w-4 mr-1"/>Scarica</Button>
                <Button size="sm" variant="outline" onClick={() => doPrint(preview.id)}><Printer className="h-4 w-4 mr-1"/>Stampa/PDF</Button>
                <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Chiudi</Button>
              </div>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="flex-1 w-full"/>
          </div>
        </div>
      )}
    </div>
  );
}
